<script lang="ts">
  import type { AccountApplication } from "@shared/types/AccountApplication";
  import type { HTTPParams } from "@shared/types/HTTP";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import Flash from "@shared/services/flashv2.js";
  import Counter from "./Counter.svelte";
  import { form as svelteForm, field } from "svelte-forms2";
  import { email as emailValidator, required } from "svelte-forms2/validators";
  import EmailInput from "@shared/svelte/Components/EmailInput.svelte";
  import FormInputError from "@shared/svelte/Components/FormInputError.svelte";

  export let id: number = null;
  export let step: number = 3;

  export let application: AccountApplication = <AccountApplication>{};

  let verificationEmail = application.owner_verification_email;

  const ownerVerificationEmail = field("owner_verification_email", "", [
    required(),
    emailValidator(),
  ]);

  const changeEmailForm = svelteForm(ownerVerificationEmail);

  const resendEmail = async () => {
    const req: HTTPParams = {
      method: "POST",
      url: `/api/retailer/account_applications/${id}/resend_email`,
    };

    try {
      await HttpPromise(req);

      Flash.now({ type: "success", message: "Email resent" });
    } catch (e) {
      return e.errors.forEach((message: string) =>
        Flash.now({ type: "error", message, rawHtml: true })
      );
    }
  };

  const saveForm = async () => {
    if (!$changeEmailForm.valid) return;
    if (!$changeEmailForm.dirty) return;

    let req: HTTPParams = {
      method: "PUT",
      url: `/api/retailer/account_applications/${id}`,
      data: {
        account_application: {
          owner_verification_email: $ownerVerificationEmail.value,
        },
      },
    };

    try {
      verificationEmail = $ownerVerificationEmail.value;
      await HttpPromise(req);
    } catch (e) {
      return e.errors.forEach((message: string) =>
        Flash.now({ type: "error", message, rawHtml: true })
      );
    }
  };

  const saveFormAndResend = async () => {
    if (!$changeEmailForm.valid) return;
    if (!$changeEmailForm.dirty) return;

    await saveForm();
    resendEmail();
    showEnterEmail = false;
  };

  let showEnterEmail = false;

  const toggleEnterEmail = () => {
    showEnterEmail = !showEnterEmail;
  };

  let extraStyles;

  $: {
    extraStyles = $LayoutStore.isDesktop ? "width: 65%;" : "width: 100%;";
  }

</script>

<main class="fizz-surface">
  <section class="fizz-container">
    <Counter {step} />
    <div
      class="fizz-flex-row-responsive fizz-inset-vertical-24 fizz-align-content-start"
      class:fizz-inset-square-16={$LayoutStore.isMobile}
      class:fizz-inset-squish-96={$LayoutStore.isDesktop}
    >
      <div class="fizz-flex-item">
        <h3 class="fizz-heading-3 fizz-stack-8">Verify your email and submit application</h3>
        <p
          class="fizz-stack-24"
          class:fizz-text-medium={$LayoutStore.isMobile}
          class:fizz-text-large={$LayoutStore.isDesktop}
        >
          We've sent an email to <span class="fizz-text-strong">{verificationEmail}</span>
        </p>
        <div
          class="fizz-flex-row-inline-8 fizz-stack-8"
          class:fizz-hide-on-small={showEnterEmail && $LayoutStore.isMobile}
          class:fizz-hide-on-large={showEnterEmail && $LayoutStore.isDesktop}
        >
          <button class="fizz-button fizz-button-small" on:click={resendEmail}>Resend email</button>
          <button class="fizz-button fizz-button-small" on:click={toggleEnterEmail}
            >Enter new email</button
          >
        </div>
        <div
          class:fizz-hide-on-small={!showEnterEmail && $LayoutStore.isMobile}
          class:fizz-hide-on-large={!showEnterEmail && $LayoutStore.isDesktop}
        >
          <div class="fizz-flex-row-8-responsive">
            <EmailInput
              classes="fizz-flow-4 fizz-stack-16 fizz-inline-block"
              {extraStyles}
              name="owner_verification_email"
              label="New email"
              field={ownerVerificationEmail}
            >
              <FormInputError form={changeEmailForm} errorName="owner_verification_email.required">
                Email address is required
              </FormInputError>
              {#if !$changeEmailForm.hasError("owner_verification_email.required")}
                <FormInputError
                  form={changeEmailForm}
                  errorName="owner_verification_email.not_an_email"
                >
                  Email address is invalid
                </FormInputError>
              {/if}
            </EmailInput>
          </div>
          <div class="fizz-flex-row-inline-8">
            <button class="fizz-button fizz-button-small" on:click={toggleEnterEmail}>Cancel</button
            >
            <button
              class="fizz-button fizz-button-small fizz-button-secondary"
              on:click={saveFormAndResend}>Send email</button
            >
          </div>
        </div>
      </div>
      <div
        class="fizz-flex-item fizz-flow-24"
        class:fizz-text-medium={$LayoutStore.isMobile}
        class:fizz-text-large={$LayoutStore.isDesktop}
      >
        <p>
          Click the link sent to <span class="fizz-text-strong">{verificationEmail}</span> to verify
          your signature and submit this application to the distributor. Review and check the status
          of your application in
          <a href="/settings/accounts?tab=applications">My Distributors</a>.
        </p>
        <p>
          For help with the application, please contact <a href="mailto:support@provi.com"
            >support@provi.com</a
          >.
        </p>
      </div>
    </div>
  </section>
</main>
